import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_FUNCTION, EMPTY_LIST } from '../../../constants/common'
import { useDate, useDateLocale } from '../../../hooks'
import { getQaSelector } from '../../../utils/common'
import { GRETA_SKELETON_DELAY_MS } from '../GretaAIChat.constants'
import {
  useGretaAIChatContext,
  useGretaAIChatDelayedFlag,
} from '../GretaAIChat.hooks'
import {
  getGretaAIChatErrorTextKey,
  getIsGretaAIChatIdle,
  getIsGretaAIChatLoading,
  getIsGretaAIChatRetryableStatus,
} from '../GretaAIChat.utils'

import { QA_NAME_GRETA_AI_CHAT_CHAT_LIST } from './GretaAIChatChatList.constants'
import {
  useGretaAIChatChatListFrozenSections,
  useGretaAIChatChatListScroll,
} from './GretaAIChatChatList.hooks'
import {
  getGretaAIChatChatListFlags,
  groupGretaAIChatChats,
} from './GretaAIChatChatList.utils'
import { GretaAIChatChatListMoreChats } from './GretaAIChatChatListMoreChats'
import { GretaAIChatChatListPlaceholder } from './GretaAIChatChatListPlaceholder'
import { GretaAIChatChatListSection } from './GretaAIChatChatListSection'
import { GretaAIChatChatListSkeleton } from './GretaAIChatChatListSkeleton'

import * as styles from './GretaAIChatChatList.module.css'

function GretaAIChatChatList({
  className = styles.chatList,
  isHeightCapped = false,
  withRowActions = true,
  onSelectChat = EMPTY_FUNCTION,
}) {
  const { t } = useTranslation()
  const now = useDate()
  const locale = useDateLocale()

  const { chatListLoader, panel } = useGretaAIChatContext()
  const {
    activeChatId,
    chatListScrollTopRef,
    chatListSeenChatIdRef,
    chatStatusById,
    isChatListOrderFrozen,
  } = panel

  const {
    chats,
    chatsStatus,
    hasMoreChats,
    hasMoreChatsFailed,
    isMoreChatsLoading,
    loadChats: reloadChats,
    loadMoreChats,
  } = chatListLoader

  const chatsLoading = getIsGretaAIChatLoading(chatsStatus)
  const chatsError = !chatsLoading && !getIsGretaAIChatIdle(chatsStatus)

  const { isEmpty, isError, isLoading, isRows } = getGretaAIChatChatListFlags({
    hasChats: chats.length > 0,
    hasError: chatsError,
    isLoading: chatsLoading,
  })

  const chatsErrorTextKey = getGretaAIChatErrorTextKey(chatsStatus)
  const isChatsErrorRetryable = getIsGretaAIChatRetryableStatus(chatsStatus)

  const isSkeletonShown = useGretaAIChatDelayedFlag(
    isLoading,
    GRETA_SKELETON_DELAY_MS
  )

  const liveSections = isRows ? groupGretaAIChatChats(chats, now) : EMPTY_LIST
  const sections = useGretaAIChatChatListFrozenSections(
    liveSections,
    isChatListOrderFrozen
  )

  const { handleListScroll, sectionsRef } = useGretaAIChatChatListScroll({
    activeChatId,
    chatListScrollTopRef,
    chatListSeenChatIdRef,
    hasMoreChats,
    hasMoreChatsFailed,
    isMoreChatsLoading,
    isRows,
    loadMoreChats,
  })

  return (
    <div
      {...getQaSelector({ value: QA_NAME_GRETA_AI_CHAT_CHAT_LIST })}
      className={className}
    >
      <div
        className={classNames(styles.sections, {
          [styles.sectionsCapped]: isHeightCapped,
        })}
        ref={sectionsRef}
        onScroll={handleListScroll}
      >
        {isSkeletonShown ? <GretaAIChatChatListSkeleton /> : null}

        {isEmpty ? (
          <GretaAIChatChatListPlaceholder
            text={t('Greta.ChatListEmptyTextKey')}
            title={t('Greta.ChatListEmptyTitleKey')}
          />
        ) : null}

        {isError ? (
          <GretaAIChatChatListPlaceholder
            retryText={t('Greta.RetryButtonKey')}
            text={t(chatsErrorTextKey)}
            title={t('Greta.ChatListErrorTitleKey')}
            onRetry={isChatsErrorRetryable ? reloadChats : null}
          />
        ) : null}

        {isRows
          ? sections.map(section => (
              <GretaAIChatChatListSection
                activeChatId={activeChatId}
                chatStatusById={chatStatusById}
                key={section.key}
                locale={locale}
                now={now}
                section={section}
                withRowActions={withRowActions}
                onSelectChat={onSelectChat}
              />
            ))
          : null}

        {isMoreChatsLoading || hasMoreChatsFailed ? (
          <GretaAIChatChatListMoreChats
            hasFailed={hasMoreChatsFailed}
            onRetry={loadMoreChats}
          />
        ) : null}
      </div>
    </div>
  )
}

export default GretaAIChatChatList
